import React, { useEffect, useRef } from "react";
import { loadChromaKeySprite } from "../../utils/spriteLoader";

/**
 * Affiche un sprite en pixel art via canvas avec chroma-key (#FF00FF → transparent)
 * et une animation de respiration (scale Y/X subtile + bobbing vertical).
 * Peut servir pour l'ennemi (face caméra) ou le joueur (dos caméra) en combat.
 */
export const BreathingSprite = ({ src, alt = "", className = "", testId = "breathing-sprite", facingLeft = false }) => {
  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);
  const animRef = useRef(null);
  const startTsRef = useRef(performance.now());

  useEffect(() => {
    if (!src || !canvasRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const off = await loadChromaKeySprite(src);
        if (cancelled || !canvasRef.current) return;
        offscreenRef.current = off;
        const target = canvasRef.current;
        const rect = target.getBoundingClientRect();
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        target.width = Math.max(64, Math.floor(rect.width * dpr));
        target.height = Math.max(64, Math.floor(rect.height * dpr));

        const draw = () => {
          if (cancelled || !canvasRef.current || !offscreenRef.current) return;
          const c = canvasRef.current;
          const ctx = c.getContext("2d");
          ctx.imageSmoothingEnabled = false;
          ctx.clearRect(0, 0, c.width, c.height);

          const now = performance.now() - startTsRef.current;
          const t = now / 700;
          const sy = 1 + Math.sin(t) * 0.04;
          const sx = 1 - Math.sin(t) * 0.02;
          const bob = Math.sin(t * 0.7) * 1.5 * dpr;

          const sw = offscreenRef.current.width;
          const sh = offscreenRef.current.height;
          const scale = Math.min(c.width / sw, c.height / sh);
          const dw = sw * scale * sx;
          const dh = sh * scale * sy;
          const dx = (c.width - dw) / 2;
          const dy = c.height - dh - bob;

          if (facingLeft) {
            ctx.save();
            ctx.translate(c.width, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(offscreenRef.current, c.width - dx - dw, dy, dw, dh);
            ctx.restore();
          } else {
            ctx.drawImage(offscreenRef.current, dx, dy, dw, dh);
          }
          animRef.current = requestAnimationFrame(draw);
        };
        draw();
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [src, facingLeft]);

  if (!src) return null;
  return (
    <canvas
      ref={canvasRef}
      data-testid={testId}
      aria-label={alt}
      className={className}
      style={{ imageRendering: "pixelated", width: "100%", height: "100%" }}
    />
  );
};
